import * as _ from "lodash";

import { memoize } from "@ctra/utils";

import { DataPointsBase } from "./DataPointsBase";

import { BarData, BarSource } from "../typings";
import { Series } from "../";

/**
 * Normalizer class for bar chart data
 */
class BarNormalizer extends DataPointsBase {
  protected entity: BarSource;
  constructor(source: BarSource) {
    super(source);
    this.entity = source;
  }

  /**
   * Process the incoming bar chart data
   */
  @memoize
  formatData(): BarData["data"] {
    /**
     * First map through and return an array of points type, then
     * order by the value desc
     */

    return _.orderBy(
      _.flatMap(this.entity.data, ({ points: [{ y: x, context }] }, key) => {
        const series = Series.create(key, this.entity.meta.seriesType);
        const seriesField = series.getField();

        return seriesField
          ? {
              /**
               * @note Bar chart X and Y are flipped as the bars are horizontal
               */
              x,
              y: key,
              seriesField: key,
              context: context
            }
          : [];
      }),
      ["x"],
      ["desc"]
    );
  }

  /**
   * Format the metadata for further processing e.g translations
   * @returns
   */
  @memoize
  formatMetadata(): BarData["meta"] {
    return _.reduce<NonNullable<BarSource["data"]>, NonNullable<BarData["meta"]>>(
      this.entity.data,
      (acc, { points: [{ y }] }, key) => {
        const series = Series.create(key, this.entity.meta.seriesType);
        const seriesField = series.getField();

        /**
         * Adding the series and axis labels to the metadata so that they can be translated by the consuming app.
         * @note Using _.setWith as it allows for a customizer as _.set creates arrays when the series are numeric values.
         */
        if (seriesField) {
          _.setWith(acc, ["series", "keys", key], seriesField, Object);
        }

        _.setWith(acc, ["axis", "x", "labels", "short", y], y, Object);

        return acc;
      },
      { ...this.entity.meta }
    );
  }
}

export { BarNormalizer };
